import { Card, CardContent, CardHeader, CardTitle } from "../../tailwindComponents/ui/card"
import { AlertCircle, ArrowDown, ArrowUp, BarChart3, Box, Calendar, DollarSign, ExternalLink, InfoIcon, Package, ShoppingBag, Tag, TrendingDown, TrendingUp, Truck } from "lucide-react";
import PerformanceDashboardService from "../../services/dashboards/PerformanceDashboard.service";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../tailwindComponents/ui/tooltip";

const defaultIconClassName = "h-4 w-4 text-muted-foreground";

const IconMapping = {
  DollarSign: <DollarSign className={defaultIconClassName} />,
  Package: <Package className={defaultIconClassName} />,
  BarChart3: <BarChart3 className={defaultIconClassName} />,
  ShoppingBag: <ShoppingBag className={defaultIconClassName} />,
  Calendar: <Calendar className={defaultIconClassName} />,
  Tag: <Tag className={defaultIconClassName} />,
  Truck: <Truck className={defaultIconClassName} />,
  Box: <Box className={defaultIconClassName} />,
  TrendingDown: (
    <TrendingDown
      className={`${defaultIconClassName} text-red-500`}
    />
  ),
  TrendingDownYellow: (
    <TrendingDown
      className={`${defaultIconClassName} text-yellow-500`}
    />
  ),
  TrendingUp: (
    <TrendingUp
      className={`${defaultIconClassName} text-emerald-500`}
    />
  ),
  TrendingUpRed: (
    <TrendingUp
      className={`${defaultIconClassName} text-red-500`}
    />
  ),
  RedAlert: (
    <AlertCircle
      className={`${defaultIconClassName} text-red-500`}
    />
  ),
  ArrowUp: <ArrowUp className={defaultIconClassName} />,
  ArrowDown: <ArrowDown className={defaultIconClassName} />,
  InfoIcon: <InfoIcon className={defaultIconClassName} />,
  ExternalLink: <ExternalLink className={defaultIconClassName} />,
};

const QuickInfoSidebarStyles = {
  'red': "w-2 rounded-l-xl bg-red-500 h-full z-50",
  'yellow': "w-2 rounded-l-xl bg-yellow-500 h-full z-50",
  'green': "w-2 rounded-l-xl bg-green-500 h-full z-50",
}

const IconCardHeader = ({
    title,
    icon,
    description,
    className = null,
    onClick = null
  }: {
    title: string;
    icon: keyof typeof IconMapping;
    description: string;
    className?: string | null;
    onClick?: (() => void) | null;
  }) => {
    const IconComponent = IconMapping[icon] || DollarSign;
  
    const headerClassName = className ? className : "flex flex-row items-center justify-between space-y-0 px-2 py-0";
  
    return (
      <CardHeader className={headerClassName}>
        {/* Left Section: Icon and Title */}
        <div className="flex items-center space-x-2">
          {IconComponent && (
            <div className="flex h-6 w-6 items-center justify-center">
              {IconComponent}
            </div>
          )}
          <CardTitle className="text-md font-medium leading-none">{title}</CardTitle>
        </div>
  
        {/* Right Section: External Link and Info Tooltip */}
        <div className="flex items-center space-x-2">
          {onClick != null && (
            <button 
              onClick={onClick}
              className="flex h-5 w-5 items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
            >
              <ExternalLink className="h-4 w-4" />
            </button>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <button className="flex h-5 w-5 items-center justify-center text-muted-foreground hover:text-foreground transition-colors cursor-help">
                <InfoIcon className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs bg-white" side="top" align="end">
              <p className="text-sm">{description}</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </CardHeader>
    );
  };

const QuickComparisonCard = ({
    title,
    value,
    comparisonValue,
    description,
    comparisonText,
    icon,
    onClick = null,
    children = null,
  }: {
    title: string;
    value: number;
    comparisonValue: number;
    description: string;
    comparisonText: string;
    icon: keyof typeof IconMapping;
    onClick?: (() => void) | null;
    children?: JSX.Element | null;
  }) => {
    // Calculate comparison color and label
    comparisonValue = value - comparisonValue;
    const comparisonColor = comparisonValue > 0 ? "text-green-500" : "text-red-500";
    const arrowDirection = comparisonValue > 0 ? "up" : "down";
  
    return (
      <Card className="rounded-xl p-2 bg-white">
        <IconCardHeader title={title} icon={icon} description={description} onClick={onClick} className={"flex flex-row items-center justify-between space-y-0 pb-2 pt-2 px-1"} />
        <CardContent className="px-1 pb-3 text-center">
          <div className="text-2xl font-bold">
            {PerformanceDashboardService.moneyFormat(value, true)}
          </div>
          {children}
          <div className="flex flex-row justify-center items-center text-xs text-muted-foreground mt-4">
            {arrowDirection == "down" ? (
              <ArrowDown className={`mr-1 h-3 w-3 ${comparisonColor}`} />
            ) : (
              <ArrowUp className={`mr-1 h-3 w-3 ${comparisonColor}`} />
            )}
            <span className={`${comparisonColor} font-medium`}>
              {`${PerformanceDashboardService.moneyFormat(Math.abs(comparisonValue), true)} ${comparisonText}`}
            </span>
          </div>
        </CardContent>
      </Card>
    );
  };

const QuickInfoCard = ({
  title,
  sidebarColor,
  value,
  tooltipText,
  description,
  color,
  icon,
  formatType = "money",
  onClick = null,
}: {
  title: string;
  value: number;
  tooltipText: string;
  description: string|JSX.Element;
  color: string;
  icon: keyof typeof IconMapping;
  sidebarColor?: keyof typeof QuickInfoSidebarStyles;
  formatType?: "money" | "percent" | "string"
  onClick?: (() => void) | null;
}) => {
  const sidebarStyle = QuickInfoSidebarStyles[sidebarColor as keyof typeof QuickInfoSidebarStyles] || null;

  return (
    <Card className="flex rounded-xl bg-white">
      {sidebarStyle && (
        <div className={sidebarStyle}></div>
      )}
      <div className="flex-1">
        <IconCardHeader title={title} icon={icon} description={tooltipText} onClick={onClick} className={"flex flex-row items-center justify-between space-y-0 pb-2 pt-4 px-3"} />
        <CardContent>
          <div className={`text-2xl font-bold text-${color}`}>
            {formatType === "money"
              ? PerformanceDashboardService.moneyFormat(value, true)
              : formatType === "percent"
                ? `${value.toFixed(0)}%`
                : value.toString()}
          </div>
          {description}
        </CardContent>
      </div>
    </Card>
  );
};

export {
    QuickComparisonCard,
    IconCardHeader,
    QuickInfoCard
  }